/**
 * Owner: Masa Bayu
 * File: qx-writer-book-spine.ts
 * Date:Time: 2026-10-16 07:28
 * Description: Spine hydrate · page number styles · add/delete page.
 */

import {
  createQXTextHeadingBlock,
  createQXTextInline,
  createQXTextParagraphBlock,
  type QXTextBlock,
  type QXTextContent,
  type QXWDocument,
} from '@qx/qxw-core';
import {
  ACADEMIC_SPINE_PROFILES,
  SPINE_ACADEMIC_BOOK,
  type WriterBookSpineItem,
  type WriterMatterZone,
} from './qx-writer-academic-spine';
import { readDraftContent } from './qx-draft-blocks';
import { inlinesToHtml } from './qx-draft-marks';
import { applySpineDocumentPageNumbers } from './qx-writer-page-number-style';

export {
  applySpineDocumentPageNumbers,
  assignSpinePageNumberStyle,
  currentSpineDocumentPage,
  currentSpineDocumentPageLabel,
  currentSpinePageNumberStyle,
  focusedSpineSheet,
  plainTextWithoutSpineChrome,
  setSpinePageNumberStyle,
} from './qx-writer-page-number-style';

export const QXW_SPINE_PAGE_CLASS = 'qxw-spine-page';
/** Word-style footer page number (not tip content). */
export const QXW_SPINE_PAGENUM_CLASS = 'qxw-spine-page__pagenum';
/** Outline-only Page N sublabel · visually hidden like Word. */
export const QXW_SPINE_PAGE_LABEL_CLASS = 'qxw-spine-page__sublabel';

export type { WriterBookSpineItem } from './qx-writer-academic-spine';

/** Default academic spine when no profile spine is supplied. */
export const WRITER_BOOK_SPINE: readonly WriterBookSpineItem[] = SPINE_ACADEMIC_BOOK;

function labelToKindMap(
  spine: readonly WriterBookSpineItem[],
): Map<string, WriterBookSpineItem> {
  return new Map(spine.map((item) => [item.label, item]));
}

function spineLabels(spine: readonly WriterBookSpineItem[]): Set<string> {
  return new Set(spine.map((item) => item.label));
}

function headingPlain(block: QXTextBlock): string {
  if (block.type !== 'heading') return '';
  return block.inlines
    .map((inline) => (inline.type === 'note-ref' ? '' : inline.text))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

function isPageSubLabel(label: string): boolean {
  return /^Page\s+\d+$/i.test(label.trim());
}

function blockInnerHtml(block: QXTextBlock, blockIndex: number): string {
  if (block.type === 'heading') {
    const tag = block.level <= 1 ? 'h1' : block.level === 2 ? 'h2' : 'h3';
    return `<${tag} id="outline_b${blockIndex}">${inlinesToHtml(block.inlines) || '<br>'}</${tag}>`;
  }
  if (block.type === 'paragraph') {
    return `<p>${inlinesToHtml(block.inlines) || '<br>'}</p>`;
  }
  return '';
}

function asTextContent(blocks: readonly QXTextBlock[]): QXTextContent {
  return { blocks: [...blocks] };
}

function sectionSeed(label: string): QXTextBlock[] {
  return [
    createQXTextHeadingBlock(1, [createQXTextInline(label)]),
    createQXTextHeadingBlock(2, [createQXTextInline('Page 1')]),
    createQXTextParagraphBlock([createQXTextInline('')]),
  ];
}

function tipSpineLabelHits(
  doc: QXWDocument,
  spine: readonly WriterBookSpineItem[],
): number {
  const content = readDraftContent(doc);
  if (!content) return 0;
  const labels = spineLabels(spine);
  const found = new Set<string>();
  for (const block of content.blocks) {
    if (block.type !== 'heading' || block.level > 1) continue;
    const label = headingPlain(block);
    if (labels.has(label)) found.add(label);
  }
  return found.size;
}

/** Tip carries all H1 bahagian labels for this spine profile. */
export function tipHasBookSpine(
  doc: QXWDocument,
  spine: readonly WriterBookSpineItem[] = WRITER_BOOK_SPINE,
): boolean {
  return tipSpineLabelHits(doc, spine) >= spine.length;
}

/** True when tip matches any academic profile spine completely. */
export function tipHasAnyBookSpine(doc: QXWDocument): boolean {
  return ACADEMIC_SPINE_PROFILES.some((spine) => tipHasBookSpine(doc, spine));
}

/** True when tip has H1 bahagian + Page N subs (any label set). */
export function tipHasPageSpineStructure(doc: QXWDocument): boolean {
  const content = readDraftContent(doc);
  if (!content) return false;
  let bahagian = 0;
  let pages = 0;
  for (const block of content.blocks) {
    if (block.type !== 'heading') continue;
    const label = headingPlain(block);
    if (!label) continue;
    if (block.level <= 1) bahagian += 1;
    else if (block.level === 2 && isPageSubLabel(label)) pages += 1;
  }
  return bahagian >= 1 && pages >= 1;
}

/** Paint as spine sheets when profile match or Page N structure present. */
export function tipUsesSpineSheets(doc: QXWDocument): boolean {
  return tipHasAnyBookSpine(doc) || tipHasPageSpineStructure(doc);
}

/**
 * Best-matching academic spine for tip HTML/Outline · full match preferred.
 */
export function resolveSpineFromTip(doc: QXWDocument): readonly WriterBookSpineItem[] {
  let best: readonly WriterBookSpineItem[] = WRITER_BOOK_SPINE;
  let bestHits = 0;
  for (const spine of ACADEMIC_SPINE_PROFILES) {
    const hits = tipSpineLabelHits(doc, spine);
    if (hits >= spine.length) return spine;
    if (hits > bestHits) {
      bestHits = hits;
      best = spine;
    }
  }
  return best;
}

/** Skeleton: each bahagian → H1 + H2 Page 1 + empty paragraph. */
export function createBookSpineContent(
  spine: readonly WriterBookSpineItem[] = WRITER_BOOK_SPINE,
): QXTextContent {
  const blocks: QXTextBlock[] = [];
  for (const item of spine) {
    blocks.push(...sectionSeed(item.label));
  }
  return asTextContent(blocks);
}

/** Prepend missing bahagian (with Page 1 sub); keep author body after. */
export function mergeBookSpineContent(
  existing: QXTextContent | null,
  spine: readonly WriterBookSpineItem[] = WRITER_BOOK_SPINE,
): QXTextContent {
  if (!existing || existing.blocks.length === 0) return createBookSpineContent(spine);
  const labels = spineLabels(spine);
  const present = new Set<string>();
  for (const block of existing.blocks) {
    if (block.type !== 'heading' || block.level > 1) continue;
    const label = headingPlain(block);
    if (labels.has(label)) present.add(label);
  }
  if (present.size >= spine.length) return existing;
  const prefix: QXTextBlock[] = [];
  for (const item of spine) {
    if (present.has(item.label)) continue;
    prefix.push(...sectionSeed(item.label));
  }
  return asTextContent([...prefix, ...existing.blocks]);
}

function kindForBahagian(
  label: string,
  byLabel: Map<string, WriterBookSpineItem>,
): string {
  return byLabel.get(label)?.kind ?? 'section';
}

function isBahagianHeading(block: QXTextBlock, label: string): boolean {
  return block.type === 'heading' && block.level <= 1 && label.length > 0;
}

/**
 * One visual sheet per Page N sub (H1 bahagian sits on first page of that bahagian).
 * Any H1 starts a bahagian — profile map only supplies kind when known.
 */
export function htmlFromBookSpineContent(
  content: QXTextContent,
  spine: readonly WriterBookSpineItem[] = WRITER_BOOK_SPINE,
): string {
  if (content.blocks.length === 0) return '<p><br></p>';
  const byLabel = labelToKindMap(spine);
  const parts: string[] = [];
  let i = 0;
  let pendingH1: {
    html: string;
    kind: string;
    label: string;
    matter: WriterMatterZone;
  } | null = null;
  let lastKind = 'body';
  let lastLabel = 'Body';
  let lastMatter: WriterMatterZone = 'main';

  while (i < content.blocks.length) {
    const block = content.blocks[i]!;
    const label = headingPlain(block);

    if (isBahagianHeading(block, label)) {
      const zone: WriterMatterZone = byLabel.get(label)?.matter ?? 'main';
      pendingH1 = {
        html: blockInnerHtml(block, i),
        kind: kindForBahagian(label, byLabel),
        label,
        matter: zone,
      };
      i += 1;
      continue;
    }

    if (block.type === 'heading' && block.level === 2 && isPageSubLabel(label)) {
      const kind = pendingH1?.kind ?? lastKind;
      const sectionLabel = pendingH1?.label ?? lastLabel;
      const matter: WriterMatterZone = pendingH1?.matter ?? lastMatter;
      const pageBlocks: string[] = [];
      if (pendingH1) {
        pageBlocks.push(pendingH1.html);
        pendingH1 = null;
      }
      pageBlocks.push(blockInnerHtml(block, i));
      i += 1;
      while (i < content.blocks.length) {
        const next = content.blocks[i]!;
        const nextLabel = headingPlain(next);
        if (isBahagianHeading(next, nextLabel)) break;
        if (next.type === 'heading' && next.level === 2 && isPageSubLabel(nextLabel)) break;
        const html = blockInnerHtml(next, i);
        if (html) pageBlocks.push(html);
        i += 1;
      }
      lastKind = kind;
      lastLabel = sectionLabel;
      lastMatter = matter;
      parts.push(
        `<section class="${QXW_SPINE_PAGE_CLASS}" data-qxw-spine-kind="${kind}" data-qxw-spine-matter="${matter}" data-qxw-spine-label="${sectionLabel}" data-qxw-page="${label}">${pageBlocks.join('')}</section>`,
      );
      continue;
    }

    const orphan: string[] = [];
    if (pendingH1) {
      orphan.push(pendingH1.html);
      lastKind = pendingH1.kind;
      lastLabel = pendingH1.label;
      lastMatter = pendingH1.matter;
      pendingH1 = null;
    }
    while (i < content.blocks.length) {
      const next = content.blocks[i]!;
      const nextLabel = headingPlain(next);
      if (isBahagianHeading(next, nextLabel)) break;
      if (next.type === 'heading' && next.level === 2 && isPageSubLabel(nextLabel)) break;
      const html = blockInnerHtml(next, i);
      if (html) orphan.push(html);
      i += 1;
    }
    if (orphan.length > 0) {
      parts.push(
        `<section class="${QXW_SPINE_PAGE_CLASS}" data-qxw-spine-kind="${lastKind}" data-qxw-spine-matter="${lastMatter}" data-qxw-spine-label="${lastLabel}" data-qxw-page="Page 1">${orphan.join('')}</section>`,
      );
    }
  }

  return parts.join('') || '<p><br></p>';
}

/** Live editor / tip spine page count for status + Outline stats. */
export function countSpinePages(
  root: HTMLElement | null | undefined,
  doc?: QXWDocument,
  spine?: readonly WriterBookSpineItem[],
): number {
  if (root) {
    const n = root.querySelectorAll(`.${QXW_SPINE_PAGE_CLASS}`).length;
    if (n > 0) return n;
  }
  if (!doc || !tipUsesSpineSheets(doc)) return 0;
  const resolved = spine ?? resolveSpineFromTip(doc);
  const content = readDraftContent(doc);
  if (!content) return resolved.length;
  let pages = 0;
  for (const block of content.blocks) {
    if (block.type === 'heading' && block.level === 2 && isPageSubLabel(headingPlain(block))) {
      pages += 1;
    }
  }
  return pages > 0 ? pages : resolved.length;
}

function spineSheetFromFocus(
  root: HTMLElement,
  focusId?: string | null,
): HTMLElement | null {
  const focusEl =
    typeof focusId === 'string' && focusId.trim()
      ? root.querySelector(`#${CSS.escape(focusId.trim())}`)
      : null;
  const sheet =
    (focusEl instanceof HTMLElement
      ? focusEl.closest(`.${QXW_SPINE_PAGE_CLASS}`)
      : null) ?? root.querySelector(`.${QXW_SPINE_PAGE_CLASS}`);
  return sheet instanceof HTMLElement ? sheet : null;
}

function sheetsForBahagian(root: HTMLElement, sheet: HTMLElement): HTMLElement[] {
  const kind = sheet.getAttribute('data-qxw-spine-kind') || 'body';
  const sectionLabel = sheet.getAttribute('data-qxw-spine-label') || 'Body';
  return Array.from(root.querySelectorAll(`.${QXW_SPINE_PAGE_CLASS}`)).filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement
      && el.getAttribute('data-qxw-spine-kind') === kind
      && el.getAttribute('data-qxw-spine-label') === sectionLabel,
  );
}

function renumberBahagianPages(sheets: readonly HTMLElement[]): void {
  sheets.forEach((el, index) => {
    const pageLabel = `Page ${index + 1}`;
    el.setAttribute('data-qxw-page', pageLabel);
    const h2 = el.querySelector(`:scope > h2.${QXW_SPINE_PAGE_LABEL_CLASS}`)
      ?? el.querySelector(':scope > h2');
    if (h2) {
      h2.textContent = pageLabel;
      h2.classList.add(QXW_SPINE_PAGE_LABEL_CLASS);
      h2.setAttribute('aria-hidden', 'true');
    }
  });
}

/**
 * Add Page N+1 sheet under the bahagian of the focused outline/page node.
 */
export function addSpinePageInEditor(
  root: HTMLElement,
  focusId?: string | null,
): { readonly ok: boolean; readonly pageLabel: string; readonly pageId: string } {
  const sheet = spineSheetFromFocus(root, focusId);
  if (!sheet) return { ok: false, pageLabel: '', pageId: '' };
  const kind = sheet.getAttribute('data-qxw-spine-kind') || 'body';
  const sectionLabel = sheet.getAttribute('data-qxw-spine-label') || 'Body';
  const kindSheets = sheetsForBahagian(root, sheet);
  const last = kindSheets[kindSheets.length - 1] ?? sheet;
  const nextIndex = kindSheets.length + 1;
  const pageLabel = `Page ${nextIndex}`;
  const pageId = `outline_page_${kind.replace(/-/g, '_')}_${nextIndex}_${Date.now().toString(36)}`;
  const section = document.createElement('section');
  section.className = QXW_SPINE_PAGE_CLASS;
  section.setAttribute('data-qxw-spine-kind', kind);
  section.setAttribute(
    'data-qxw-spine-matter',
    sheet.getAttribute('data-qxw-spine-matter') || 'main',
  );
  section.setAttribute('data-qxw-spine-label', sectionLabel);
  section.setAttribute('data-qxw-page', pageLabel);
  const sectionId = sheet.getAttribute('data-qxw-section-id');
  if (sectionId) section.setAttribute('data-qxw-section-id', sectionId);
  const h2 = document.createElement('h2');
  h2.id = pageId;
  h2.textContent = pageLabel;
  const p = document.createElement('p');
  p.append(document.createElement('br'));
  section.append(h2, p);
  last.after(section);
  applySpineDocumentPageNumbers(root);
  if (typeof section.scrollIntoView === 'function') {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  return { ok: true, pageLabel, pageId };
}

/**
 * Delete focused page sheet · keep ≥1 page per bahagian · renumber Page N.
 */
export function deleteSpinePageInEditor(
  root: HTMLElement,
  focusId?: string | null,
): { readonly ok: boolean; readonly pageLabel: string } {
  const sheet = spineSheetFromFocus(root, focusId);
  if (!sheet) return { ok: false, pageLabel: '' };
  const kindSheets = sheetsForBahagian(root, sheet);
  if (kindSheets.length <= 1) return { ok: false, pageLabel: '' };
  const pageLabel = sheet.getAttribute('data-qxw-page') || '';
  const index = kindSheets.indexOf(sheet);
  const neighbor = kindSheets[index + 1] ?? kindSheets[index - 1] ?? null;
  const kind = sheet.getAttribute('data-qxw-spine-kind') || 'body';
  const sectionLabel = sheet.getAttribute('data-qxw-spine-label') || 'Body';
  // Keep bahagian H1 when deleting the sheet that holds it.
  const bahagianH1 = sheet.querySelector(':scope > h1');
  if (bahagianH1 && neighbor) neighbor.prepend(bahagianH1);
  sheet.remove();
  const remaining = Array.from(root.querySelectorAll(`.${QXW_SPINE_PAGE_CLASS}`)).filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement
      && el.getAttribute('data-qxw-spine-kind') === kind
      && el.getAttribute('data-qxw-spine-label') === sectionLabel,
  );
  renumberBahagianPages(remaining);
  applySpineDocumentPageNumbers(root);
  if (neighbor && typeof neighbor.scrollIntoView === 'function') {
    neighbor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  return { ok: true, pageLabel };
}
